import { MIN_COINS, COIN_PRICE_CENTS, formatEuro } from "@/lib/salonturbo";

export type FaqItem = { question: string; answer: string };
export type FaqGroup = { id: string; title: string; intro?: string; items: FaqItem[] };

export const faqGroups: FaqGroup[] = [
  {
    id: "erste-schritte",
    title: "Erste Schritte",
    intro: "Was du zum Start brauchst und wie lange es dauert.",
    items: [
      {
        question: "Wie lange dauert die Registrierung?",
        answer:
          "Die Ersteinrichtung dauert etwa 3 bis 5 Minuten. Du gibst Name, Kontaktdaten, Salonadresse und Öffnungszeiten ein, lädst mindestens vier Coins auf und schon kannst du deinen ersten Tag boosten.",
      },
      {
        question: "Was brauche ich für die Einrichtung?",
        answer:
          "Nur deine Salon-Daten, Öffnungszeiten und ein paar Bilder. Ein Google-Ads-Konto oder technisches Vorwissen ist nicht nötig – wir kümmern uns um die komplette Kampagnen-Technik.",
      },
      {
        question: "Kann ich meine eigenen Bilder verwenden?",
        answer:
          "Ja. Entweder schlagen wir dir passende Bilder von deiner Website vor, die du direkt übernehmen kannst, oder du lädst eigene Fotos hoch. Wir empfehlen eigene Salon-Aufnahmen, weil sie am authentischsten wirken.",
      },
      {
        question: "Gibt es eine Mindestlaufzeit oder ein Abo?",
        answer:
          "Nein. Du kaufst Coins, wenn du sie brauchst, und boostest die Tage, die du füllen möchtest. Es gibt keinen Vertrag, keine monatliche Gebühr und keine Kündigungsfrist.",
      },
    ],
  },
  {
    id: "boosten",
    title: "Boosten & Kalender",
    intro: "Wie die Tagesauswahl funktioniert.",
    items: [
      {
        question: "Wie schnell läuft eine Kampagne nach dem Boosten an?",
        answer:
          "Deine Google-Ads-Kampagne wird bereits im Vorfeld von uns angelegt und geprüft. Sobald du einen Tag boostest, schalten wir sie um Mitternacht aktiv. In der Regel läuft die Werbung innerhalb weniger Minuten.",
      },
      {
        question: "Warum kann ich am Tag der Registrierung nicht sofort heute boosten?",
        answer:
          "Google prüft jede neue Kampagne einmalig – das dauert in der Regel bis zu 24 Stunden. Deshalb ist am Tag der Einrichtung der nächste Tag der früheste Boost-Tag. Danach kannst du auch den heutigen Tag auswählen.",
      },
      {
        question: "Wann genau läuft die Werbung an einem Boost-Tag?",
        answer:
          "Die Anzeigen starten um Mitternacht am gewählten Tag und laufen bis zwei Stunden vor Ende deiner Öffnungszeiten. So erreichst du Kunden, die noch am selben Tag einen Termin buchen wollen.",
      },
      {
        question: "Warum lassen sich manche Tage nicht auswählen?",
        answer:
          "Tage, an denen dein Salon laut Öffnungszeiten geschlossen ist, lassen sich nicht boosten – und der heutige Tag nicht mehr, wenn die Werbezeit schon abgelaufen ist. Stimmen die Ruhetage nicht, kannst du die Öffnungszeiten jederzeit in den Salon-Daten anpassen.",
      },
      {
        question: "Wie weit im Voraus kann ich planen?",
        answer:
          "Bis zu 30 Tage. Viele Salons loggen sich einmal pro Woche ein und markieren die Tage, die noch Luft haben.",
      },
      {
        question: "Was passiert an Tagen, an denen ich nicht booste?",
        answer:
          "An Nicht-Boost-Tagen bleibt deine persönliche Salon-Landingpage online, allerdings ohne den Dringlichkeits-Hinweis „Heute noch Termine frei“. Du zahlst nur für die Tage, die du aktiv boostest.",
      },
    ],
  },
  {
    id: "coins",
    title: "Coins & Rechnung",
    intro: "Preise, Zahlung und Storno.",
    items: [
      {
        question: "Was kostet ein Coin?",
        answer: `Ein Coin kostet ${formatEuro(COIN_PRICE_CENTS)} netto zzgl. Umsatzsteuer und entspricht einem geboosteten Tag. Werbebudget, Kampagne und Salon-Seite sind darin enthalten.`,
      },
      {
        question: "Wie viele Coins muss ich mindestens kaufen?",
        answer: `Der Einstieg ist ab ${MIN_COINS} Coins möglich – das reicht für vier geboostete Tage. Danach lädst du jederzeit auf, wann und wie viel du möchtest.`,
      },
      {
        question: "Wie bezahle ich?",
        answer:
          "Über den Stripe-Checkout kannst du bequem mit Karte, Apple Pay, Google Pay oder SEPA bezahlen. Rechnungen und Zahlungsübersichten findest du jederzeit in deinem Dashboard.",
      },
      {
        question: "Verfallen meine Coins?",
        answer:
          "Nein. Gekaufte Coins bleiben in deinem Konto, bis du sie für einen Boost-Tag einlöst.",
      },
      {
        question: "Was passiert, wenn ich einen Tag versehentlich geboostet habe?",
        answer:
          "Solange die Kampagne für den Tag noch nicht aktiv gelaufen ist, kannst du den Boost stornieren und der Coin wird dir wieder gutgeschrieben. Bereits laufende oder vergangene Tage können nicht rückgängig gemacht werden.",
      },
    ],
  },
  {
    id: "google-ads",
    title: "Google Ads & Salon-Seite",
    intro: "Wo deine Werbung läuft und wie sie aussieht.",
    items: [
      {
        question: "Brauche ich ein eigenes Google-Ads-Konto?",
        answer:
          "Nein. Die Kampagnen laufen über unser zentrales Google-Ads-Konto. Du musst dich bei Google nicht selbst anmelden oder etwas einrichten – wir übernehmen die komplette Technik für dich.",
      },
      {
        question: "Werden die Anzeigen wirklich nur in meiner Region geschaltet?",
        answer:
          "Ja. Deine Anzeigen werden auf Basis deiner Salon-Adresse ausgespielt und zielen auf Kunden ab, die sich gerade in deiner Nähe nach einem spontanen Friseurtermin umsehen.",
      },
      {
        question: "Auf welche Suchanfragen laufen die Anzeigen?",
        answer:
          "Auf dringlichkeitsnahe Suchbegriffe wie „Friseur heute“, „Friseurtermin heute“ oder „Friseur in der Nähe offen“ – kombiniert mit deinem Ort. Genau dort entscheidet sich, wer heute noch anruft.",
      },
      {
        question: "Wohin führt die Anzeige?",
        answer:
          "Auf deine eigene Salon-Seite bei SalonTurbo. Sie zeigt deinen Salon, Bilder, Öffnungszeiten und einen großen Anruf-Button. An Boost-Tagen zusätzlich den Hinweis, dass heute noch Termine frei sind.",
      },
      {
        question: "Muss ich die Anzeige selbst pflegen?",
        answer:
          "Nein. Sobald du Salon-Daten, Öffnungszeiten oder Bilder änderst, aktualisiert das System die Google-Anzeige automatisch. Eine Vorschau der Anzeige findest du in den Salon-Daten.",
      },
    ],
  },
  {
    id: "support",
    title: "Technik & Support",
    items: [
      {
        question: "Wie erreiche ich den Support?",
        answer:
          "Über den Support-Button in der Navigation und im Dashboard. Du beschreibst kurz dein Anliegen, kannst einen Screenshot anhängen und bekommst eine Ticket-Nummer. Wir antworten in der Regel innerhalb eines Werktags.",
      },
      {
        question: "Was passiert, wenn ein Boost-Tag technisch nicht ausgeliefert wird?",
        answer:
          "Wenn ein geboosteter Tag aus technischen Gründen nicht wie vorgesehen laufen konnte, schreiben wir dir den Coin gut. Das System erkennt Fehlbuchungen in vielen Fällen automatisch.",
      },
      {
        question: "Kann ich meinen Zugang wieder löschen?",
        answer:
          "Ja. Schreib uns über den Support und wir deaktivieren deinen Salon samt Salon-Seite. Nicht eingelöste Coins rechnen wir nach den AGB ab.",
      },
    ],
  },
];

export const allFaqs: FaqItem[] = faqGroups.flatMap((group) => group.items);

/** Kurze Auswahl für die Startseite – die volle Liste steht auf /faq. */
export const homeFaqs: FaqItem[] = [
  allFaqs[0]!,
  faqGroups[1]!.items[0]!,
  faqGroups[1]!.items[5]!,
  faqGroups[2]!.items[1]!,
  faqGroups[3]!.items[0]!,
  faqGroups[3]!.items[1]!,
];

export function faqJsonLd(items: FaqItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: { "@type": "Answer", text: item.answer },
    })),
  };
}
